import React, { useCallback, useState, useContext } from 'react';
import _ from 'lodash';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useModal } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { LoadSaveService } from "twia-capability-quoteandbind-residential";
import wizardStyles from 'twia-portals-wizard-components-ui/common/TWIAWizard.module.scss'
import { ENTQuestionSetComponent as QuestionSetComponent_Ext } from 'ent-capability-quoteandbind-questionsets-ui';
import messages from './QualificationPage.messages.js'
import metadata from './QualificationPage.metadata.json5';
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil.js';
import { wizardMessages } from 'tfpa-portals-wizard-components-ui';

const LOB_PATH = {
    CommercialProperty: 'lobData.commercialProperty.preQualQuestionSets.children[0].value',
    propertyEligibility: 'lobData.commercialProperty.preQualQuestionSets.children[1].value',
};

function TWIAQuoteCPQualificationPage(props) {
    const { id, wizardData: submissionVM, updateWizardData } = props;
    const translator = useContext(TranslatorContext);
    const { authHeader } = useAuthentication();

    const { isComponentValid, onValidate, initialValidation } = useValidation(id);
    const [showErrors, setShowErrors] = useState(false);
    const lobCode = _.get(submissionVM.value, 'baseData.productCode');

    const { showAlert, showConfirm } = useModal();

    const onNext = useCallback(async() => {
        if (!isComponentValid) {
            setShowErrors(true);
            return false
        }
        
        // DE15769
        _.set(submissionVM, 'lobData.commercialProperty.coverages.buildingCoverages', []);
        _.set(submissionVM, 'lobData.commercialProperty.coverables.locations', []);
        _.unset(submissionVM.value, 'bindData');
        updateWizardData(submissionVM);

        try {
            const updatedSubmission = await LoadSaveService.validatePreQualAndUpdateDraftSubmission_TWIA(
                submissionVM.value,
                authHeader
            );

            _.extend(submissionVM.value, updatedSubmission);

            updateWizardData(submissionVM);
    
            return submissionVM;
            
        } catch (err) {    
            if(parseErrorMessage(err.baseError).includes("DTO")){
                ErrorPopupUtil.showSaveErrorPopup(showAlert, true);
            }else{
                showAlert({
                    title: messages.TWIACommercialQualificationElegibilityErrorTitle,
                    message: parseErrorMessage(err.baseError),
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.close,
                }).catch(_.noop);
            }       
                
            return false; 
        }
    }, [isComponentValid, submissionVM, authHeader, updateWizardData, showAlert])

    const parseErrorMessage = (message) => _.split(message, 'ErrorMessage: ')[2]

    const onPrevious = useCallback(() => {
        if (!isComponentValid) {
           return showConfirm({
                title: wizardMessages.TWIAWizardModalTitle,
                message: wizardMessages.TWIAWizardModalMessage,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: wizardMessages.TWIAWizardModalConfirm,
                cancelButtonText: wizardMessages.TWIAWizardModalCancel,
            }).then((results) => {
                if (results === 'cancel' || results === 'close') {
                    setShowErrors(true);
                    return false;
                }                
                return true;
            }, _.noop)
        }        
    }, [isComponentValid]);

    const formatTitleTypekey = (msg) => {
        return translator({id: `questionkey.QuestionSet_${msg}.Name`})
    }

    const overrideProps = {
        generalQuestionSet: {
            path: LOB_PATH[lobCode],
            title: formatTitleTypekey(_.get(submissionVM, `${LOB_PATH[lobCode]}.code`)),
            onValidate,
            showErrors
        },
        propertyQuestionSet: {
            path: LOB_PATH['propertyEligibility'],
            title: translator(messages.TWIACommercialQualificationPropertyTitle),
            onValidate,
            showErrors
        },
    }
    const resolvers = {
        resolveClassNameMap: wizardStyles,
        resolveComponentMap: { questionset: QuestionSetComponent_Ext },
        resolveCallbackMap: {
            onValidate
        }
    }

    const skip = useCallback(async vm => {
        const validAnswers =  _.get(submissionVM, 'lobData.commercialProperty.preQualAnswersValid_TWIA.value');
        return (await initialValidation()) && validAnswers;
    });

    return (
        <WizardPage
            onNext={onNext}
            skipWhen={skip}
            onPrevious={onPrevious}
        >
            
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showErrors}
            />
        </WizardPage>
    )
}

export default TWIAQuoteCPQualificationPage
